import React, { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { reactPlugin } from "@datadog/browser-rum-react";
import { datadogRum } from "@datadog/browser-rum";
import packageJson from "../../package.json";
import AuthContext from "../contexts/AuthContext";
import { hasAdminPermission } from "../utils/auth";

export const DatadogRumProvider = ({ children }) => {
  const { user, authTokens } = useContext(AuthContext);
  const location = useLocation();
  const isInitialized = useRef(false);
  const sessionRecordingStarted = useRef(false);

  const isAdminUser =
    user &&
    authTokens?.access_token &&
    hasAdminPermission(authTokens.access_token);
  const isOnWarehouseOrStorePage =
    location.pathname.includes("/warehouse") ||
    location.pathname.includes("/store");

  useEffect(() => {
    // Initialize DataDog RUM only for admin users
    if (!isInitialized.current && isAdminUser) {
      try {
        datadogRum.init({
          applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID,
          clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
          site: process.env.REACT_APP_DATADOG_SITE,
          service: "lego-takeback-clients",
          env: process.env.REACT_APP_ENV,
          version: packageJson.version,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          startSessionReplayRecordingManually: true,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: "allow",
          plugins: [reactPlugin({ router: true })],
        });

        console.log("✅ Datadog RUM initialized successfully for admin user");
        isInitialized.current = true;
      } catch (error) {
        console.error("❌ Datadog RUM initialization failed:", error);
      }
    }
  }, [isAdminUser]);

  useEffect(() => {
    // Start session recording only on warehouse/store pages (if DataDog is initialized)
    if (isInitialized.current && isOnWarehouseOrStorePage) {
      if (!sessionRecordingStarted.current) {
        try {
          datadogRum.startSessionReplayRecording();
          console.log(
            "✅ Datadog session recording started for warehouse/store page"
          );
          sessionRecordingStarted.current = true;
        } catch (error) {
          console.error("❌ Failed to start Datadog session recording:", error);
        }
      }
    } else if (sessionRecordingStarted.current && !isOnWarehouseOrStorePage) {
      // Stop recording when leaving warehouse/store pages
      try {
        datadogRum.stopSessionReplayRecording();
        console.log(
          "⏹️ Datadog session recording stopped (left warehouse/store page)"
        );
        sessionRecordingStarted.current = false;
      } catch (error) {
        console.error("❌ Failed to stop Datadog session recording:", error);
      }
    }
  }, [isOnWarehouseOrStorePage]);

  return <>{children}</>;
};
